import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { WidgetChip } from '@sorare/core/src/atoms/ui/Chip/WidgetChip/index.tsx';
import { WidgetButton } from '@sorare/core/src/atoms/ui/WidgetButton/index.tsx';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs/index.tsx';
import { idFromObject } from '@sorare/core/src/gql/idFromObject.ts';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog.ts';
import { isType, isTypes } from '@sorare/core/src/lib/gql/index.ts';

import { getProgress } from '#src/components/series/lineup/GuessTheScoreTaskLineup/GuessTheScoreTaskLineupProgress/index.tsx';

import { VsIcon } from './VsIcon/index.tsx';
import type { GuessTheScoreBadge_taskInterface } from './__generated__/index.graphql.ts';
import badgeBg from './assets/guess-the-score-bg.webp';

type GuessTheScoreBadgeState = 'new' | 'inProgress' | 'claimable';

const Circle = styled(Horizontal).attrs({ center: true })`
  width: calc(6 * var(--unit));
  height: calc(6 * var(--unit));
  border-radius: 50%;
  background: url(${badgeBg}) no-repeat center / cover;
  padding: 2px;
  position: relative;
  overflow: hidden;
`;
const PlayerImg = styled.img`
  width: 40px;
  object-fit: cover;
  position: absolute;
  top: 8px;

  &.home {
    left: -8px;
  }
  &.away {
    right: -8px;
  }
`;
const Vs = styled.div`
  position: relative;
  z-index: 1;
  & > * {
    height: 30px;
  }
`;

const getState = (
  task: GuessTheScoreBadge_taskInterface
): GuessTheScoreBadgeState => {
  if (!isType(task, 'GuessTheScoreTask')) {
    return 'new';
  }
  if (task.aasmState === ManagerProgressionTaskState.COMPLETED) {
    return 'claimable';
  }
  if (task.taskLineups?.some(lineup => lineup.hasLiveGames)) {
    return 'inProgress';
  }
  return 'new';
};

const getLiveLineup = (task: GuessTheScoreBadge_taskInterface) => {
  if (!isType(task, 'GuessTheScoreTask')) {
    return null;
  }
  const lineups = task.taskLineups ?? [];
  return lineups.find(lineup => lineup.hasLiveGames) ?? lineups[0] ?? null;
};

const renderBottom = (
  state: GuessTheScoreBadgeState,
  score: number | null,
  target: number | null
) => {
  if (state === 'claimable') {
    return <WidgetChip state="reward" />;
  }
  if (state === 'inProgress') {
    return (
      <WidgetChip state="progress" progress={getProgress(score, target)}>
        {Math.round(score ?? 0)}
      </WidgetChip>
    );
  }
  return <WidgetChip state="new" />;
};

type Props = {
  task: GuessTheScoreBadge_taskInterface;
};

export const GuessTheScoreBadge = ({ task }: Props) => {
  const openDialog = useOpenDialog();
  const game = isType(task, 'GuessTheScoreTask') ? task.game : null;

  const homeTeam = isTypes(game?.homeTeam, ['Club', 'NationalTeam'])
    ? game?.homeTeam
    : null;
  const awayTeam = isTypes(game?.awayTeam, ['Club', 'NationalTeam'])
    ? game?.awayTeam
    : null;
  const homeTopPlayer = homeTeam?.topPlayers?.[0] ?? null;
  const awayTopPlayer = awayTeam?.topPlayers?.[0] ?? null;
  const state = getState(task);
  const liveLineup = getLiveLineup(task);
  const liveScore = liveLineup?.score ?? null;
  const target = liveLineup?.targetScore ?? null;

  return (
    <WidgetButton
      onClick={() => {
        if (isType(task, 'GuessTheScoreTask')) {
          openDialog(DialogKey.guessTheScoreTask, idFromObject(task.id));
        }
      }}
      chip={renderBottom(state, liveScore, target)}
    >
      <Circle>
        {!!homeTopPlayer && (
          <PlayerImg
            className="home"
            src={homeTopPlayer.squaredPictureUrl}
            alt=""
            role="presentation"
          />
        )}
        {!!awayTopPlayer && (
          <PlayerImg
            className="away"
            src={awayTopPlayer.squaredPictureUrl}
            alt=""
            role="presentation"
          />
        )}
        <Vs>
          <VsIcon />
        </Vs>
      </Circle>
    </WidgetButton>
  );
};

GuessTheScoreBadge.fragments = {
  taskInterface: gql`
    fragment GuessTheScoreBadge_taskInterface on TaskInterface {
      id
      ... on GuessTheScoreTask {
        id
        aasmState
        taskLineups {
          id
          hasLiveGames
          score
          targetScore
        }
        game {
          id
          homeTeam {
            slug
            ... on Club {
              slug
              topPlayers {
                slug
                squaredPictureUrl
              }
            }
            ... on NationalTeam {
              slug
              topPlayers {
                slug
                squaredPictureUrl
              }
            }
          }
          awayTeam {
            slug
            ... on Club {
              slug
              topPlayers {
                slug
                squaredPictureUrl
              }
            }
            ... on NationalTeam {
              slug
              topPlayers {
                slug
                squaredPictureUrl
              }
            }
          }
        }
      }
    }
  ` as TypedDocumentNode<GuessTheScoreBadge_taskInterface>,
};
